import { apiAuth, apiPublic, axiosInitPromise } from './apiBase';

/**
 * API Service class to handle requests with both public and authenticated instances.
 */
class ApiService {
    // Cache the axios instances to avoid re-initializing on every request
    static async initialize() {
        await axiosInitPromise;
    }

    /**
     * Perform a GET request.
     */
    static async get(url, params = {}, isAuth = true, responseType = 'json') {
        await this.initialize(); // Ensure Axios instances are initialized
        try {
            // Choose the appropriate Axios instance and add token if auth is true
            const config = {
                params,
                responseType,
            };

            if (isAuth) {
                const token = localStorage.getItem('authToken'); // Get token from localStorage
                if (token) {
                    config.headers = {
                        Authorization: `Bearer ${token}`,
                    };
                }
            }

            const response = await (isAuth ? apiAuth : apiPublic).get(url, config);
            return response; // Return the entire response object
        } catch (error) {
            this.handleApiError(error); // Handle and log the error
            throw error;
        }
    }

    /**
     * Perform a POST request.
     */
    static async post(url, data, isAuth = true, contentType = 'application/json') {
        await this.initialize();
        try {
            const config = {
                headers: {
                    'Content-Type': contentType,
                },
            };

            if (isAuth) {
                const token = localStorage.getItem('authToken'); // Get token from localStorage
                if (token) {
                    config.headers['Authorization'] = `Bearer ${token}`;
                }
            }

            const response = await (isAuth ? apiAuth : apiPublic).post(url, data, config);
            return response;
        } catch (error) {
            this.handleApiError(error);
            throw error;
        }
    }

    /**
     * Perform a PUT request.
     */
    static async put(url, data, isAuth = true) {
        await this.initialize();
        try {
            const config = {};

            if (isAuth) {
                const token = localStorage.getItem('authToken'); // Get token from localStorage
                if (token) {
                    config.headers = {
                        Authorization: `Bearer ${token}`,
                    };
                }
            }

            const response = await (isAuth ? apiAuth : apiPublic).put(url, data, config);
            return response;
        } catch (error) {
            this.handleApiError(error);
            throw error;
        }
    }

    /**
     * Perform a DELETE request.
     */
    static async delete(url, isAuth = true) {
        await this.initialize();
        try {
            const config = {};

            if (isAuth) {
                const token = localStorage.getItem('authToken'); // Get token from localStorage
                if (token) {
                    config.headers = {
                        Authorization: `Bearer ${token}`,
                    };
                }
            }

            const response = await (isAuth ? apiAuth : apiPublic).delete(url, config);
            return response;
        } catch (error) {
            this.handleApiError(error);
            throw error;
        }
    }

    /**
     * General method to handle API errors.
     */
    static handleApiError(error) {
        if (error.response) {
            // Handle API errors (response from server)
            console.error(`API Error: ${error.response.status} - ${error.response.statusText}`);
            console.error('Response data:', error.response.data);
            if (error.response.status === 401) {
                // Handle unauthorized errors (e.g., token expiry, re-authenticate)
                console.error('Authentication failed. Redirecting to login...');
                // You can add logic to redirect to the login page or refresh the token here
            }
        } else if (error.request) {
            // No response received
            console.error('API Error: No response received');
            console.error('Request data:', error.request);
        } else {
            // General error while setting up the request
            console.error('API Error:', error.message);
        }
    }
}

export default ApiService;
